// Motor de cohete: construcción validada, curva de empuje y masa. Ver docs/theory/motors.md.
import { P0 } from '../atmosphere/isa';
import type { MotorMassProfile } from '../rocket/motor';

export interface Motor {
  readonly id: string;
  readonly designation: string;
  readonly manufacturer: string;
  /** Diámetro (m). */
  readonly diameter: number;
  /** Longitud (m). */
  readonly length: number;
  /** Retardos de eyección disponibles (s). */
  readonly delays: readonly number[];
  /** Sin carga de eyección. */
  readonly plugged: boolean;
  /** Masa de propelente (kg). */
  readonly propellantMass: number;
  /** Masa total al inicio (kg). */
  readonly totalMass: number;
  /** Diámetro de salida de la tobera (m), si el archivo lo indica. */
  readonly nozzleExitDiameter?: number;
  /** Tiempos de la curva (s), empezando en 0. */
  readonly time: readonly number[];
  /** Empujes de la curva (N), uno por tiempo. */
  readonly thrust: readonly number[];
  /** Impulso acumulado (N·s) en cada punto de la curva. */
  readonly cumulativeImpulse: readonly number[];
  readonly burnTime: number;
  readonly totalImpulse: number;
  readonly averageThrust: number;
  readonly maxThrust: number;
  readonly impulseClass: string;
}

export interface MotorInput {
  readonly designation: string;
  readonly manufacturer: string;
  readonly diameter: number;
  readonly length: number;
  readonly delays: readonly number[];
  readonly plugged: boolean;
  readonly propellantMass: number;
  readonly totalMass: number;
  readonly nozzleExitDiameter?: number;
  /** Puntos tal como vienen en el archivo; se antepone (0, 0) si el primero no está en t = 0. */
  readonly points: ReadonlyArray<readonly [time: number, thrust: number]>;
}

export type MotorResult = { readonly ok: true; readonly motor: Motor } | { readonly ok: false; readonly error: string };

/** Fracción del diámetro del motor usada si se desconoce la salida de la tobera (supuesto de diseño). */
export const DEFAULT_NOZZLE_FRACTION = 0.6;

const finitePositive = (v: number): boolean => Number.isFinite(v) && v > 0;

export function impulseClassOf(totalImpulse: number): string {
  if (totalImpulse <= 0.3125) return '1/8A';
  if (totalImpulse <= 0.625) return '1/4A';
  if (totalImpulse <= 1.25) return '1/2A';
  const n = Math.ceil(Math.log2(totalImpulse / 1.25 - 1e-12));
  return String.fromCharCode('A'.charCodeAt(0) + Math.max(n - 1, 0));
}

const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Identificador de catálogo de un motor, a partir de fabricante y designación. */
export const motorIdOf = (manufacturer: string, designation: string): string => slug(`${manufacturer}-${designation}`);

export function createMotor(input: MotorInput): MotorResult {
  const fail = (error: string): MotorResult => ({ ok: false, error });
  const name = input.designation;

  if (name.trim() === '') return fail('El motor no tiene designación');
  if (!finitePositive(input.diameter)) return fail(`${name}: el diámetro debe ser mayor que cero`);
  if (!finitePositive(input.length)) return fail(`${name}: la longitud debe ser mayor que cero`);
  if (!finitePositive(input.propellantMass)) return fail(`${name}: la masa de propelente debe ser mayor que cero`);
  if (!finitePositive(input.totalMass) || input.totalMass < input.propellantMass) {
    return fail(`${name}: la masa total debe ser mayor o igual que la de propelente`);
  }

  const pts: Array<readonly [number, number]> = [...input.points];
  if (pts.length === 0) return fail(`${name}: no tiene puntos de la curva de empuje`);
  if (pts.some(([t, f]) => !Number.isFinite(t) || !Number.isFinite(f))) return fail(`${name}: la curva contiene valores no numéricos`);
  if (pts[0]![0] < 0) return fail(`${name}: la curva tiene tiempos negativos`);
  if (pts[0]![0] > 0) pts.unshift([0, 0]);
  if (pts.length < 2) return fail(`${name}: la curva necesita al menos dos puntos`);
  for (let i = 1; i < pts.length; i++) {
    if (!(pts[i]![0] > pts[i - 1]![0])) return fail(`${name}: los tiempos de la curva deben ser estrictamente crecientes (punto ${i + 1})`);
  }
  if (pts.some(([, f]) => f < 0)) return fail(`${name}: la curva tiene empujes negativos`);
  if (pts[pts.length - 1]![1] !== 0) return fail(`${name}: la curva no termina en empuje cero (¿archivo truncado?)`);

  const time = pts.map(([t]) => t);
  const thrust = pts.map(([, f]) => f);
  const cumulativeImpulse: number[] = [0];
  for (let i = 1; i < time.length; i++) {
    cumulativeImpulse.push(cumulativeImpulse[i - 1]! + ((thrust[i]! + thrust[i - 1]!) / 2) * (time[i]! - time[i - 1]!));
  }
  const totalImpulse = cumulativeImpulse[cumulativeImpulse.length - 1]!;
  if (!(totalImpulse > 0)) return fail(`${name}: el impulso total debe ser mayor que cero`);
  const burnTime = time[time.length - 1]!;

  const motor: Motor = {
    id: motorIdOf(input.manufacturer, name),
    designation: name,
    manufacturer: input.manufacturer,
    diameter: input.diameter,
    length: input.length,
    delays: input.delays,
    plugged: input.plugged,
    propellantMass: input.propellantMass,
    totalMass: input.totalMass,
    ...(input.nozzleExitDiameter !== undefined && input.nozzleExitDiameter > 0 ? { nozzleExitDiameter: input.nozzleExitDiameter } : {}),
    time,
    thrust,
    cumulativeImpulse,
    burnTime,
    totalImpulse,
    averageThrust: totalImpulse / burnTime,
    maxThrust: Math.max(...thrust),
    impulseClass: impulseClassOf(totalImpulse),
  };
  return { ok: true, motor };
}

/** Índice i tal que time[i] <= t < time[i+1] (búsqueda binaria). Requiere 0 <= t < burnTime. */
function segment(m: Motor, t: number): number {
  let lo = 0;
  let hi = m.time.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (m.time[mid]! <= t) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Empuje a `t` s de la ignición, con interpolación lineal; cero fuera de la combustión. */
export function thrustAt(m: Motor, t: number): number {
  if (!(t >= 0) || t >= m.burnTime) return 0;
  const i = segment(m, t);
  const t0 = m.time[i]!;
  const f0 = m.thrust[i]!;
  return f0 + ((m.thrust[i + 1]! - f0) * (t - t0)) / (m.time[i + 1]! - t0);
}

/** Impulso entregado hasta `t` (N·s). */
export function impulseAt(m: Motor, t: number): number {
  if (!(t > 0)) return 0;
  if (t >= m.burnTime) return m.totalImpulse;
  const i = segment(m, t);
  return m.cumulativeImpulse[i]! + ((m.thrust[i]! + thrustAt(m, t)) / 2) * (t - m.time[i]!);
}

/** Masa total (carcasa + propelente restante) a `t` s de la ignición. */
export function massAt(m: Motor, t: number): number {
  const casing = m.totalMass - m.propellantMass;
  return casing + m.propellantMass * (1 - impulseAt(m, t) / m.totalImpulse);
}

/** Área de salida de la tobera (m²). */
export function nozzleExitArea(m: Motor): number {
  const d = m.nozzleExitDiameter ?? DEFAULT_NOZZLE_FRACTION * m.diameter;
  return (Math.PI * d * d) / 4;
}

/**
 * Empuje efectivo a presión ambiente `ambientPressure` (Pa): F + A_e (P0 − Pa) mientras hay empuje.
 * Si `exitArea` se omite se usa el área de tobera del motor.
 */
export function effectiveThrust(m: Motor, t: number, ambientPressure: number, exitArea: number = nozzleExitArea(m)): number {
  const f = thrustAt(m, t);
  if (!(f > 0)) return 0;
  return Math.max(0, f + exitArea * (P0 - ambientPressure));
}

/** Adaptador al contrato del modelo de cohete. */
export const toMassProfile = (m: Motor): MotorMassProfile => ({
  diameter: m.diameter,
  length: m.length,
  massAt: (t) => massAt(m, t),
});
